import { AdaptivePlayback, PlaybackEstimates, type PlaybackClip, type PlaybackLoad } from "./adaptivePlayback";

export const MP3_SAMPLE_RATE = 44100;
export interface Mp3Writer {
  encode(audio: AudioBuffer): Promise<void>;
  finish(): Promise<Blob>;
  close(): void;
}
export interface Mp3Progress { percent: number; completed: number; total: number; message: string }

export function mp3Filename(filename?: string | null) {
  const base = (filename || "screenplay").replace(/\.(fountain|fdx|txt|pdf|mp3)$/i, "")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-").replace(/[. ]+$/g, "").trim().slice(0, 150);
  return `${base || "screenplay"}.mp3`;
}

/** One encoder for the entire screenplay: no per-sentence encoder padding,
 * no realtime recording, and no full-script uncompressed buffer in memory. */
export async function exportScreenplayMp3(input: {
  clips: PlaybackClip[];
  prepare: (index: number) => Promise<PlaybackLoad>;
  signal: AbortSignal;
  onProgress: (progress: Mp3Progress) => void;
  createWriter: (signal: AbortSignal) => Promise<Mp3Writer>;
  estimates?: PlaybackEstimates;
}): Promise<Blob> {
  const { signal, clips } = input;
  if (!clips.length) throw new Error("This screenplay has no spoken text to export.");
  signal.throwIfAborted();
  const queue = new AdaptivePlayback(clips, input.prepare, input.estimates ?? new PlaybackEstimates());
  const stop = () => queue.stop();
  signal.addEventListener("abort", stop, { once: true });
  let writer: Mp3Writer | undefined;
  const report = (completed: number, message: string) => input.onProgress({
    percent: Math.min(99, Math.floor(completed * 100 / clips.length)), completed, total: clips.length, message,
  });
  try {
    report(0, "Starting MP3 export… Keep this page open.");
    writer = await input.createWriter(signal);
    signal.throwIfAborted();
    for (let i = 0; i < clips.length; i++) {
      report(i, `Preparing sentence ${i + 1} of ${clips.length}…`);
      const audio = await queue.get(i, () => {});
      signal.throwIfAborted();
      await writer.encode(audio);
      signal.throwIfAborted();
      queue.release(i);
      report(i + 1, `Encoded ${i + 1} of ${clips.length} sentences.`);
    }
    report(clips.length, "Finishing MP3 file…");
    const blob = await writer.finish();
    signal.throwIfAborted();
    if (blob.type !== "audio/mpeg" || blob.size < 100) throw new Error("The MP3 encoder returned an empty or invalid file.");
    input.onProgress({ percent: 100, completed: clips.length, total: clips.length, message: "MP3 ready to download." });
    return blob;
  } finally {
    signal.removeEventListener("abort", stop);
    queue.stop(); writer?.close();
  }
}
